/**
 * DocumentsModerationPanel — Iter63b-bis
 *
 * Bandeau "X documents en attente" + Drawer modération.
 * Visible archi/moe_principal seulement (gating via prop `canModerate`).
 *
 * Endpoints :
 *  - GET /api/chantiers/{cid}/documents/pending-moderation
 *  - POST /api/documents/{did}/moderate
 *  - GET /api/chantiers/{cid}/equipe-projet (pour autocomplete users)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { ClipboardText, X, FilePdf, Files, Image as ImageIcon, FileDoc, FileXls } from "@phosphor-icons/react";
import { Button } from "../../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { toast } from "sonner";
import DocumentVisibilityForm, { ROLE_LABELS, ROLE_BADGES } from "../DocumentVisibilityForm";
import { libelleRole } from "../../../lib/libelles";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS_LOCAL = ROLE_LABELS;
const ROLE_BADGES_LOCAL = ROLE_BADGES;
const DEFAULT_VISIBLE = ["archi", "moe_principal", "moe_membre"];

function getFileIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".pdf")) return FilePdf;
  if (/\.(jpg|jpeg|png|gif|webp|svg)$/.test(n)) return ImageIcon;
  if (/\.(xlsx|xls|csv)$/.test(n)) return FileXls;
  if (/\.(docx|doc)$/.test(n)) return FileDoc;
  return Files;
}

function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ============= Approve sub-modal =============
function ApproveDialog({ open, onClose, doc, equipe, onConfirm, saving }) {
  const [visibleTo, setVisibleTo] = useState(DEFAULT_VISIBLE);
  const [specificUsers, setSpecificUsers] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setVisibleTo([...DEFAULT_VISIBLE]);
      setSpecificUsers([]);
      setNote("");
    }
  }, [open, doc?.id]);

  const handleSubmit = () => {
    onConfirm({
      action: "approve",
      visible_to: visibleTo,
      visible_to_specific_users: specificUsers,
      note: note || null,
    });
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Approuver le document
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[#2C2C2C] font-medium truncate">{doc.name}</div>
          <div className="text-xs text-[#6B6B6B]">
            Uploadé par {doc.uploaded_by_name || "?"}
            {doc.uploader_role_projet ? ` (${ROLE_LABELS_LOCAL[doc.uploader_role_projet] || doc.uploader_role_projet})` : null}
          </div>
          <div className="border-t border-[#E5E5E5] pt-3">
            <DocumentVisibilityForm
              visibleTo={visibleTo}
              setVisibleTo={setVisibleTo}
              visibleToSpecificUsers={specificUsers}
              setVisibleToSpecificUsers={setSpecificUsers}
              note={note}
              setNote={setNote}
              equipeOverride={equipe}
              testIdPrefix="approve"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-none" data-testid="approve-cancel">
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={saving || visibleTo.length === 0 && specificUsers.length === 0} className="bg-[#2D6A4F] hover:bg-[#3B8B6B] rounded-none" data-testid="approve-confirm">
              {saving ? "Approbation…" : "Approuver"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============= Reject sub-modal =============
function RejectDialog({ open, onClose, doc, onConfirm, saving }) {
  const [note, setNote] = useState("");

  useEffect(() => { if (open) setNote(""); }, [open, doc?.id]);

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Rejeter le document
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-[#2C2C2C] font-medium truncate">{doc.name}</div>
          <div className="text-xs text-[#6B6B6B]">
            Uploadé par {doc.uploaded_by_name || "?"}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#6B6B6B]">Raison du rejet (optionnelle mais recommandée)</Label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full mt-1 border border-[#E5E5E5] rounded-none p-2 text-sm focus:outline-none focus:border-[#2C2C2C]"
              placeholder="Ex : Document hors-sujet, à re-uploader dans la bonne section"
              data-testid="reject-note"
            />
          </div>
          <div className="text-xs text-[#6B6B6B] bg-[#FFF8F2] border-l-2 border-amber-400 px-3 py-2">
            ⚠️ Le document restera visible à l'uploader avec le statut « rejeté ». Il pourra le supprimer ou le re-soumettre.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-none" data-testid="reject-cancel">
              Annuler
            </Button>
            <Button onClick={() => onConfirm({ action: "reject", note: note || null })} disabled={saving} className="bg-[#8B3A3A] hover:bg-[#A04545] text-white rounded-none" data-testid="reject-confirm">
              {saving ? "Rejet…" : "Rejeter"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============= Drawer principal =============
function ModerationDrawer({ open, onClose, chantierId, pendingDocs, equipe, onModerated }) {
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleModerate = async (doc, payload) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/documents/${doc.id}/moderate`, payload, { withCredentials: true });
      toast.success(payload.action === "approve" ? "Document approuvé" : "Document rejeté");
      setApproving(null);
      setRejecting(null);
      onModerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de modération");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="moderation-drawer">
        <SheetHeader>
          <SheetTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Documents à modérer ({pendingDocs.length})
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {pendingDocs.length === 0 ? (
            <div className="text-sm text-[#6B6B6B] italic py-8 text-center" data-testid="moderation-empty">
              Aucun document en attente. Bravo !
            </div>
          ) : (
            pendingDocs.map((doc) => {
              const Icon = getFileIcon(doc.name);
              return (
                <div key={doc.id} className="border border-[#E5E5E5] p-3 flex items-start gap-3" data-testid={`pending-doc-${doc.id}`}>
                  <Icon size={32} className="text-[#6B6B6B] flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2C2C2C] truncate" title={doc.name}>{doc.name}</div>
                    <div className="text-xs text-[#6B6B6B] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>par {doc.uploaded_by_name || "?"}</span>
                      {doc.uploader_role_projet ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm uppercase ${ROLE_BADGES_LOCAL[doc.uploader_role_projet] || "bg-[#E0E0DB]"}`}>
                          {libelleRole(doc.uploader_role_projet)}
                        </span>
                      ) : null}
                      <span>·</span>
                      <span>{fmtDate(doc.uploaded_at)}</span>
                      {doc.file_size ? <><span>·</span><span>{fmtSize(doc.file_size)}</span></> : null}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      {doc.url ? (
                        <a
                          href={`${API_URL}${doc.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#1E3A5F] underline hover:text-[#2C2C2C]"
                          data-testid={`pending-preview-${doc.id}`}
                        >
                          Aperçu
                        </a>
                      ) : null}
                      <button
                        onClick={() => setApproving(doc)}
                        className="text-xs px-2 py-1 bg-[#2D6A4F] text-white hover:bg-[#3B8B6B] rounded-sm"
                        data-testid={`pending-approve-${doc.id}`}
                      >
                        ✓ Approuver
                      </button>
                      <button
                        onClick={() => setRejecting(doc)}
                        className="text-xs px-2 py-1 bg-[#8B3A3A] text-white hover:bg-[#A04545] rounded-sm"
                        data-testid={`pending-reject-${doc.id}`}
                      >
                        ✕ Rejeter
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <ApproveDialog
          open={!!approving}
          onClose={() => setApproving(null)}
          doc={approving}
          equipe={equipe}
          onConfirm={(p) => handleModerate(approving, p)}
          saving={saving}
        />
        <RejectDialog
          open={!!rejecting}
          onClose={() => setRejecting(null)}
          doc={rejecting}
          onConfirm={(p) => handleModerate(rejecting, p)}
          saving={saving}
        />
      </SheetContent>
    </Sheet>
  );
}

// ============= Bandeau orange + state principal =============
export default function DocumentsModerationPanel({ chantierId, canModerate = true, onChange }) {
  const [pendingDocs, setPendingDocs] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!chantierId || !canModerate) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/chantiers/${chantierId}/documents/pending-moderation`, { withCredentials: true });
      setPendingDocs(Array.isArray(r.data) ? r.data : []);
    } catch {
      setPendingDocs([]);
    } finally {
      setLoading(false);
    }
  }, [chantierId, canModerate]);

  const fetchEquipe = useCallback(async () => {
    if (!chantierId || !canModerate) return;
    try {
      const r = await axios.get(`${API_URL}/api/projets/${chantierId}/moe`, { withCredentials: true });
      setEquipe(Array.isArray(r.data) ? r.data : []);
    } catch {
      setEquipe([]);
    }
  }, [chantierId, canModerate]);

  useEffect(() => { fetchPending(); fetchEquipe(); }, [fetchPending, fetchEquipe]);

  const onModerated = useCallback(() => {
    fetchPending();
    if (typeof onChange === "function") onChange();
  }, [fetchPending, onChange]);

  if (!canModerate || pendingDocs.length === 0) return null;

  return (
    <>
      <div
        className="border-l-4 border-amber-400 bg-amber-50 px-4 py-3 mb-4 flex items-center justify-between gap-3"
        data-testid="moderation-banner"
      >
        <div className="flex items-center gap-2 text-sm text-amber-900">
          <ClipboardText size={20} weight="fill" />
          <span>
            <strong>{pendingDocs.length}</strong> document{pendingDocs.length > 1 ? "s" : ""} en attente de modération
          </span>
        </div>
        <Button
          onClick={() => setDrawerOpen(true)}
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-none"
          data-testid="moderation-open"
        >
          Modérer maintenant
        </Button>
      </div>
      <ModerationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        chantierId={chantierId}
        pendingDocs={pendingDocs}
        equipe={equipe}
        onModerated={onModerated}
      />
    </>
  );
}
